// Visual skin: "glass" (Liquid Glass, the default since 31.10.2026), the brand CI v2 ("ci") or "Bold Blobs" ("blobs").
// ?theme=glass / ?theme=blobs / ?theme=ci in a link applies to that visit only (a plain link always opens the default).
// Settings › Units, language, design saves the choice on this device (needed for the Home Screen app).
// The skin only changes looks; every screen keeps the same data and behaviour.
// The glass skin also follows the time of day: <html data-time="morning|day|evening|night"> (sky, light or dark glass).
// Only the home screen shows the real time of day; every other screen keeps the day sky (owner's decision 03.11.2026).
// Going home and back, the sky layers cross-fade (CSS opacity transition), so the change is smooth.
import { useSyncExternalStore } from 'react';

export type Skin = 'ci' | 'blobs' | 'glass';
export type TimeOfDay = 'morning' | 'day' | 'evening' | 'night';
const SKINS: Skin[] = ['ci', 'blobs', 'glass'];
const THEME_COLOR: Record<Skin, string> = { ci: '#21253F', blobs: '#15161A', glass: '#F3F7FD' };
const GLASS_THEME: Record<TimeOfDay, string> = { morning: '#FFE3D2', day: '#F7FAFF', evening: '#B4B2EC', night: '#0A1330' };
const isSkin = (v: unknown): v is Skin => SKINS.includes(v as Skin);
const KEY = 'bp:skin-choice'; // the old "bp:skin" key also saved link visits; it is ignored and removed
const listeners = new Set<() => void>();
let current: Skin = 'glass';
let time: TimeOfDay = timeNow();
/** True while the home screen is open: only then the sky follows the clock. */
let onHome = location.pathname === '/';
const shown = (): TimeOfDay => (onHome || fixedTime ? time : 'day');

/** Morning 5–10, day 10–17, evening 17–21, night 21–5 (the phone's own clock). */
export function timeNow(d = new Date()): TimeOfDay {
  const h = d.getHours();
  return h >= 5 && h < 10 ? 'morning' : h >= 10 && h < 17 ? 'day' : h >= 17 && h < 21 ? 'evening' : 'night';
}

function paint() {
  const root = document.documentElement;
  const t = shown();
  root.dataset.time = t;
  const dark = current !== 'glass' || t === 'night';
  root.style.colorScheme = dark ? 'dark' : 'light';
  document.querySelector('meta[name="color-scheme"]')?.setAttribute('content', dark ? 'dark' : 'light');
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', current === 'glass' ? GLASS_THEME[t] : THEME_COLOR[current]);
}

function apply(s: Skin) {
  current = s;
  document.documentElement.dataset.skin = s;
  const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"][type="image/svg+xml"]');
  if (icon) icon.href = s === 'blobs' ? '/favicon-blobs.svg' : '/favicon.svg';
  paint();
  listeners.forEach((l) => l());
}

/** Called once before the first render. */
export function initSkin() {
  let s: Skin = 'glass';
  try {
    localStorage.removeItem('bp:skin');
    const saved = localStorage.getItem(KEY);
    if (isSkin(saved)) s = saved;
  } catch { /* private mode */ }
  const q = new URLSearchParams(location.search).get('theme');
  if (isSkin(q)) s = q;
  const qt = new URLSearchParams(location.search).get('time'); // ?time=night for testing
  if (qt === 'morning' || qt === 'day' || qt === 'evening' || qt === 'night') { time = qt; fixedTime = true; }
  apply(s);
  const tick = () => { if (fixedTime) return; const t = timeNow(); if (t !== time) { time = t; paint(); listeners.forEach((l) => l()); } };
  window.setInterval(tick, 60_000);
  document.addEventListener('visibilitychange', tick);
}
let fixedTime = false;

/** The time of day the screen shows: the clock on the home screen, "day" elsewhere. */
export function useTimeOfDay(): TimeOfDay {
  return useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, shown);
}

/** The app shell tells whether the home screen is open. */
export function setOnHome(home: boolean) {
  if (home === onHome) return;
  onHome = home;
  paint();
  listeners.forEach((l) => l());
}

export function setSkin(s: Skin) {
  try { localStorage.setItem(KEY, s); } catch { /* ignore */ }
  apply(s);
}

export function useSkin(): Skin {
  return useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => current);
}

// Glass skin art: the felt illustrations are swapped for a white-and-blue 3D set (public/art/glass, white background,
// shown with mix-blend-mode: multiply). `glassKey` picks the icon where one felt plate stands for several things.
const GLASS_ART: Record<string, string> = {
  '/art/felt/comfort-lamb.webp': 'comfort',
  '/art/felt/phase-morning.webp': 'phase',
  '/art/felt/track-nap.webp': 'track-sleep',
  '/art/felt/track-diaper.webp': 'track-diaper',
  '/art/felt/track-medicine.webp': 'track-medicine',
  '/art/felt/track-growth.webp': 'track-growth',
  '/art/plates/felt-bed.webp': 'step-bed',
  '/art/plates/felt-book.webp': 'step-book',
  '/art/plates/felt-feed.webp': 'step-feed',
  '/art/plates/felt-pajamas.webp': 'step-pajamas',
  '/art/plates/felt-song.webp': 'step-song',
  '/art/plates/tub.webp': 'step-bath',
  '/logo/baby-plan-mark.svg': '/art/glass/sprout.svg',
};
const GLASS_SOUND: Record<string, string> = { 'sound-hair': 'sound-hairdryer', 'sound-music': 'sound-musicbox' };

/** Bump when the glass icons are redrawn: phones and Cloudflare keep /art for a week under the same name
 *  (2 = transparent cut-outs, 3 Nov 2026). */
const ART_V = 2;
/** URL of a glass icon (public/art/glass/<key>.webp), versioned so a redrawn set reaches every phone at once. */
export const glassIcon = (key: string) => `/art/glass/${key}.webp?v=${ART_V}`;

/** The picture to show for `felt` in the current skin. */
export function art(felt: string, glassKey?: string): string {
  if (current !== 'glass') return felt;
  const k = (glassKey && (GLASS_SOUND[glassKey] ?? glassKey)) || GLASS_ART[felt];
  if (!k) return felt;
  return k.startsWith('/') ? k : glassIcon(k);
}
